import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { selectEffectiveAgentInfo } from 'src/store'
import { Menu, Home, Mail, BookOpen, HelpCircle } from 'lucide-react'
import { Button } from 'src/components/ui/button'
import { ScrollArea } from 'src/components/ui/scroll-area'
import { Sheet, SheetContent, SheetTrigger } from 'src/components/ui/sheet'
import { Separator } from 'src/components/ui/separator'
import { cn } from 'src/lib/utils'
import { getNavigationForPermission } from 'src/_nav'
import { NavSection, NavGroup as NewNavGroup } from 'src/components/navigation'
import { BRAND_COLORS, BRAND_PRESETS } from 'src/styles/brandColors'

const NavItem = ({ item, isCollapsed, isNested = false }) => {
  const location = useLocation()
  const isActive = location.pathname === item.to

  return (
    <Link to={item.to}>
      <Button
        variant="ghost"
        className={cn(
          "w-full justify-start text-sm font-medium",
          isCollapsed ? "justify-center px-2" : "pl-9",
          isActive
            ? cn("bg-gradient-to-r text-white shadow-md", BRAND_COLORS.gradient.primaryDark, `hover:${BRAND_COLORS.gradient.primaryDarkHover}`, BRAND_COLORS.shadow.strong)
            : cn(BRAND_COLORS.accent.lightHover, BRAND_COLORS.accent.darkHover)
        )}
        title={isCollapsed ? item.name : undefined}
      >
        {!isCollapsed && <span>{item.name}</span>}
      </Button>
    </Link>
  )
}

const SidebarContentNew = ({ sections, isCollapsed }) => {
  const location = useLocation()
  const isHomeActive = location.pathname === '/dashboard' || location.pathname === '/'

  return (
    <ScrollArea className="flex-1 py-2">
      <div className="space-y-1 px-2">
        {/* Home link at the top */}
        <Link to="/dashboard">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 font-semibold text-sm",
              isCollapsed ? "justify-center px-2" : "px-4",
              isHomeActive
                ? cn("bg-gradient-to-r text-white shadow-md", BRAND_COLORS.gradient.primaryDark, `hover:${BRAND_COLORS.gradient.primaryDarkHover}`, BRAND_COLORS.shadow.strong)
                : cn(BRAND_COLORS.accent.lightHover, BRAND_COLORS.accent.darkHover)
            )}
            title={isCollapsed ? "Home" : undefined}
          >
            <Home className="h-[18px] w-[18px] shrink-0" />
            {!isCollapsed && <span>Home</span>}
          </Button>
        </Link>

        {/* Section navigation */}
        {sections.map((section) => (
          <NavSection key={section.id} section={section} isCollapsed={isCollapsed}>
            {section.items.map((item, idx) => {
              if (item.type === 'group') {
                return (
                  <NewNavGroup key={idx} group={item} isCollapsed={isCollapsed}>
                    {item.items.map((groupItem, groupIdx) => (
                      <NavItem key={groupIdx} item={groupItem} isCollapsed={isCollapsed} isNested={true} />
                    ))}
                  </NewNavGroup>
                )
              }
              return <NavItem key={idx} item={item} isCollapsed={isCollapsed} />
            })}
          </NavSection>
        ))}

        {/* Separator */}
        <Separator className="my-2" />

        {/* Help / Developer Center / Contact Us are intentionally NOT part of
            navSections in _nav.js — they're utility links rendered directly
            here, outside the sidebar-routes-audit.test.js scope (which only
            audits navSections <-> routes.js consistency). */}

        {/* Help link — opens the standalone, publicly-accessible Help page
            in a new tab so readers keep their admin console context. */}
        <a href="/#/help" target="_blank" rel="noopener noreferrer">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 text-sm",
              isCollapsed ? "justify-center px-2" : "px-4",
              BRAND_COLORS.accent.lightHover,
              BRAND_COLORS.accent.darkHover
            )}
            title={isCollapsed ? "Help" : undefined}
          >
            <HelpCircle className="h-[18px] w-[18px] shrink-0" />
            {!isCollapsed && <span>Help</span>}
          </Button>
        </a>

        {/* Developer Center link */}
        <a href="https://api.voipbin.net/docs/" target="_blank" rel="noopener noreferrer">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 text-sm",
              isCollapsed ? "justify-center px-2" : "px-4",
              BRAND_COLORS.accent.lightHover,
              BRAND_COLORS.accent.darkHover
            )}
            title={isCollapsed ? "Developer Center" : undefined}
          >
            <BookOpen className="h-[18px] w-[18px] shrink-0" />
            {!isCollapsed && <span>Developer Center</span>}
          </Button>
        </a>

        {/* Contact Us link */}
        <a href="mailto:support@voipbin.net">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3 text-sm",
              isCollapsed ? "justify-center px-2" : "px-4",
              BRAND_COLORS.accent.lightHover,
              BRAND_COLORS.accent.darkHover
            )}
            title={isCollapsed ? "Contact Us" : undefined}
          >
            <Mail className="h-[18px] w-[18px] shrink-0" />
            {!isCollapsed && <span>Contact Us</span>}
          </Button>
        </a>
      </div>
    </ScrollArea>
  )
}


export const Sidebar = () => {
  const dispatch = useDispatch()
  const sidebarCollapsed = useSelector((state) => state.ui.sidebarUnfoldable)
  const agentInfo = useSelector(selectEffectiveAgentInfo)

  const sections = React.useMemo(() => {
    if (!agentInfo) return []

    if (agentInfo.permission & 0x0001) {
      return getNavigationForPermission(0x0001)
    } else if (agentInfo.permission & 0x0020) {
      return getNavigationForPermission(0x0020)
    } else if (agentInfo.permission & 0x0040) {
      return getNavigationForPermission(0x0040)
    } else if (agentInfo.permission & 0x0010) {
      return getNavigationForPermission(0x0010)
    }

    return []
  }, [agentInfo])

  const toggleSidebar = () => {
    dispatch({ type: 'ui/update', payload: { sidebarUnfoldable: !sidebarCollapsed } })
  }

  // Desktop sidebar
  const DesktopSidebar = (
    <div className={cn(
      "hidden lg:flex flex-col transition-all duration-300 overflow-hidden",
      sidebarCollapsed ? "w-16" : "w-60"
    )}>
      <div className="flex items-center justify-between p-4">
        {!sidebarCollapsed && (
          <Link to="/dashboard" className="flex items-center gap-2 font-semibold hover:text-primary transition-colors">
            <img
              src="/voipbin-original.png"
              alt="VoIPBin"
              className={cn("h-8 w-8", BRAND_PRESETS.logo)}
            />
            <span>VoIPBin</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className={cn(sidebarCollapsed && "mx-auto")}
          aria-label="Toggle sidebar"
        >
          <Menu className="h-4 w-4" />
        </Button>
      </div>
      <SidebarContentNew sections={sections} isCollapsed={sidebarCollapsed} />
    </div>
  )

  // Mobile sidebar
  const MobileSidebar = (
    <div className="lg:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className={cn("w-60 p-0 rounded-r-2xl", BRAND_PRESETS.sidebarBg)}>
          <div className="flex items-center p-4">
            <Link to="/dashboard" className="flex items-center gap-2 font-semibold hover:text-primary transition-colors">
              <img
                src="/voipbin-original.png"
                alt="VoIPBin"
                className={cn("h-8 w-8", BRAND_PRESETS.logo)}
              />
              <span>VoIPBin</span>
            </Link>
          </div>
          <SidebarContentNew sections={sections} isCollapsed={false} />
        </SheetContent>
      </Sheet>
    </div>
  )

  return (
    <>
      {DesktopSidebar}
      {MobileSidebar}
    </>
  )
}
